import React, {useState, useEffect} from 'react';
import debounce from 'debounce';
import moment from 'moment';
import {Formik} from 'formik';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import DataTableWithFiltersView from './DataTableWithFilters-view';
import SplashScreen from 'common/components/SplashScreen';
import {
  elevatorPresence,
  shumaStatuses,
  taskStatuses,
  taskTypes
} from '../../constants/options';
import {
  getShumaPropertyTypes,
  getAllShumaTypes,
  getFirmUsers
} from "./actions/DataTableWithFiltersOptionsActions";

const DELAY = 500;

const DataTableWithFilters = ({
                                page,
                                setPage,
                                getListHandler,
                                data,
                                columns,
                                cities,
                                streets,
                                updateStreets,
                                pendingIndicator = <SplashScreen/>,
                                additionalData = {},
                                filtersPresistanceOptions = null,
                                allowSearchByCustomerName,
                                allowSearchByShumaID,
                                getListForExcel,
                                preparedDataForExcel,
                                headersForExcel,
                                excelFileName = '',
                                module,
                                ...rest
                              }) => {
  const dispatch = useDispatch();

  const [sortColumnName, setSortColumnName] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const restoreFilter = (key, defaultValue = '', isDate = false) => {
    if (filtersPresistanceOptions) {
      const prevValue = filtersPresistanceOptions.previousFilters[key];
      if (prevValue) {
        return isDate ? new Date(prevValue) : prevValue;
      } else {
        return defaultValue;
      }
    } else {
      return defaultValue;
    }
  };

  const [city, setCity] = useState(restoreFilter('city'));
  const [street, setStreet] = useState(restoreFilter('street'));
  const [gush, setGush] = useState(restoreFilter('gush'));
  const [helka, setHelka] = useState(restoreFilter('helka'));
  const [fromDate, setFromDate] = useState(restoreFilter('fromDate', '', true));
  const [toDate, setToDate] = useState(restoreFilter('toDate', '', true));
  const [customerName, setCustomerName] = useState(restoreFilter('customerName'));
  const [shumaID, setShumaID] = useState(restoreFilter('shumaID'));
  const [shumaTypeID, setShumaTypeID] = useState(restoreFilter('shumaTypeID'));
  const [propertyTypeID, setPropertyTypeID] = useState(restoreFilter('propertyTypeID'));
  const [statusTypeID, setStatusTypeID] = useState(restoreFilter('statusTypeID'));
  const [fromRoomCount, setFromRoomCount] = useState(restoreFilter('fromRoomCount'));
  const [toRoomCount, setToRoomCount] = useState(restoreFilter('toRoomCount'));
  const [fromRegisteredArea, setFromRegisteredArea] = useState(restoreFilter('fromRegisteredArea'));
  const [toRegisteredArea, setToRegisteredArea] = useState(restoreFilter('toRegisteredArea'));
  const [elevator, setElevator] = useState(restoreFilter('elevator'));
  const [responsibleUserID, setResponsibleUserID] = useState(restoreFilter('responsibleUserID'));
  const [taskStatusID, setTaskStatusID] = useState(restoreFilter('taskStatusID'));
  const [taskTypeID, setTaskTypeID] = useState(restoreFilter('taskTypeID'));
  const [fromDueDate, setFromDueDate] = useState(restoreFilter('fromDueDate', null, true));
  const [toDueDate, setToDueDate] = useState(restoreFilter('toDueDate', null, true));

  const handleSearchChange = (key, handler) => value => {
    setPage(0);
    handler(value);
    if (filtersPresistanceOptions) {
      filtersPresistanceOptions.saveFilter({[key]: value});
    }
  };

  const handleCityChange = value => {
    setPage(0);
    setCity(value);
    if (filtersPresistanceOptions) {
      filtersPresistanceOptions.saveFilter({'city': value});
    }
    handleSearchReset('street', setStreet)();
    if (Boolean(value.value)) {
      updateStreets(value.value);
    } else {
      handleCityReset();
    }
  };

  const handleSearchReset = (key, handler) => () => {
    handleSearchChange(key, handler)(null);
  };

  const handleCityReset = () => {
    handleSearchReset('city', setCity)();
    handleSearchReset('street', setStreet)();
  };

  const requestBody = module === 'TasksList'
    ? {
      shumaID: -1,
      taskID: -1,
      taskStatusID: (taskStatusID && taskStatusID.value) ? taskStatusID.value : -1,
      taskTypeID: (taskTypeID && taskTypeID.value) ? taskTypeID.value : -1,
      fromDueDate: (fromDueDate) ? moment(fromDueDate).format('YYYY-MM-DD') : '',
      toDueDate: (toDueDate) ? moment(toDueDate).format('YYYY-MM-DD') : '',
      cityID: (city) ? city.value : -1,
      propertyTypeID: (propertyTypeID) ? propertyTypeID.value : -1,
      customer: customerName,
      requestedPageNumber: page,
      ...additionalData
    }
    : {
      cityID: (city) ? city.value : -1,
      streetID: (street) ? street.value : -1,
      gush,
      helka,
      fromDate: (fromDate) ? moment(fromDate).format('YYYY-MM-DD') : '',
      toDate: (toDate) ? moment(toDate).format('YYYY-MM-DD') : '',
      customerName,
      shumaID: (shumaID) ? shumaID : -1,
      shumaTypeID: (shumaTypeID) ? shumaTypeID.value : -1,
      propertyTypeID: (propertyTypeID) ? propertyTypeID.value : -1,
      statusTypeID: (statusTypeID) ? statusTypeID.value : -1,
      fromRoomCount: fromRoomCount ? fromRoomCount : -1,
      toRoomCount: toRoomCount ? toRoomCount : -1,
      fromRegisteredArea: fromRegisteredArea ? fromRegisteredArea : -1,
      toRegisteredArea: toRegisteredArea ? toRegisteredArea : -1,
      elevator: (elevator) ? elevator.value : -1,
      responsibleUserID: (responsibleUserID) ? responsibleUserID.value : -1,
      requestedPageNumber: page,
      ...additionalData
    };

  const refreshGrid = debounce(() => {
    getListHandler({...requestBody, sortBy: sortColumnName, sortDirection});
  }, DELAY);

  useEffect(() => {
    refreshGrid();
  }, [
    page,
    city,
    street,
    gush,
    helka,
    fromDate,
    toDate,
    customerName,
    shumaID,
    shumaTypeID,
    propertyTypeID,
    statusTypeID,
    fromRoomCount,
    toRoomCount,
    fromRegisteredArea,
    toRegisteredArea,
    elevator,
    responsibleUserID,
    taskStatusID,
    taskTypeID,
    fromDueDate,
    toDueDate
  ]);

  const getOptions = () => {
    if (propertyTypes.length < 1) {
      dispatch(getShumaPropertyTypes())
    }
    if (shumaTypes.length < 1) {
      dispatch(getAllShumaTypes())
    }
    if (users.length < 1 && module === 'InspectedFirmList') {
      dispatch(getFirmUsers())
    }
  };

  useEffect(() => getOptions(), []);

  const {propertyTypes, shumaTypes, users} = useSelector(
    (state) => ({
      propertyTypes: state.dataTableWithFiltersOptionsReducer.propertyTypes,
      shumaTypes: state.dataTableWithFiltersOptionsReducer.shumaTypes,
      users: state.dataTableWithFiltersOptionsReducer.users,
    }),
    shallowEqual
  );

  const handleSortChange = (column, direction) => {
    setSortColumnName(column);
    setSortDirection(direction);

    getListHandler({...requestBody, sortBy: column, sortDirection: direction})
  };

  return (data !== null)
    ? (
      <Formik
        initialValues={{}}
        onSubmit={() => {
        }}
        enableReinitialize={true}
      >
        <DataTableWithFiltersView
          search={{
            city: {
              value: city,
              onChange: handleCityChange,
              onReset: handleCityReset,
            },
            street: {
              value: street,
              onChange: handleSearchChange('street', setStreet),
              onReset: handleSearchReset('street', setStreet),
            },
            gush: {
              value: gush,
              onChange: handleSearchChange('gush', setGush),
            },
            helka: {
              value: helka,
              onChange: handleSearchChange('helka', setHelka),
            },
            fromDate: {
              value: fromDate,
              onChange: handleSearchChange('fromDate', setFromDate),
              onReset: handleSearchReset('fromDate', setFromDate),
            },
            toDate: {
              value: toDate,
              onChange: handleSearchChange('toDate', setToDate),
              onReset: handleSearchReset('toDate', setToDate),
            },
            customerName: {
              value: customerName,
              onChange: handleSearchChange('customerName', setCustomerName),
            },
            shumaID: {
              value: shumaID,
              onChange: handleSearchChange('shumaID', setShumaID),
            },
            shumaTypeID: {
              value: shumaTypeID,
              onChange: handleSearchChange('shumaTypeID', setShumaTypeID),
              onReset: handleSearchReset('shumaTypeID', setShumaTypeID),
            },
            propertyTypeID: {
              value: propertyTypeID,
              onChange: handleSearchChange('propertyTypeID', setPropertyTypeID),
              onReset: handleSearchReset('propertyTypeID', setPropertyTypeID),
            },
            statusTypeID: {
              value: statusTypeID,
              onChange: handleSearchChange('statusTypeID', setStatusTypeID),
              onReset: handleSearchReset('statusTypeID', setStatusTypeID),
            },
            fromRoomCount: {
              value: fromRoomCount,
              onChange: handleSearchChange('fromRoomCount', setFromRoomCount),
            },
            toRoomCount: {
              value: toRoomCount,
              onChange: handleSearchChange('toRoomCount', setToRoomCount),
            },
            fromRegisteredArea: {
              value: fromRegisteredArea,
              onChange: handleSearchChange('fromRegisteredArea', setFromRegisteredArea),
            },
            toRegisteredArea: {
              value: toRegisteredArea,
              onChange: handleSearchChange('toRegisteredArea', setToRegisteredArea),
            },
            elevator: {
              value: elevator,
              onChange: handleSearchChange('elevator', setElevator),
              onReset: handleSearchReset('elevator', setElevator),
            },
            responsibleUserID: {
              value: responsibleUserID,
              onChange: handleSearchChange('responsibleUserID', setResponsibleUserID),
              onReset: handleSearchReset('responsibleUserID', setResponsibleUserID),
            },
            taskStatusID: {
              value: taskStatusID,
              onChange: handleSearchChange('taskStatusID', setTaskStatusID),
              onReset: handleSearchReset('taskStatusID', setTaskStatusID),
            },
            taskTypeID: {
              value: taskTypeID,
              onChange: handleSearchChange('taskTypeID', setTaskTypeID),
              onReset: handleSearchReset('taskTypeID', setTaskTypeID),
            },
            fromDueDate: {
              value: fromDueDate,
              onChange: handleSearchChange('fromDueDate', setFromDueDate),
              onReset: handleSearchReset('fromDueDate', setFromDueDate),
            },
            toDueDate: {
              value: toDueDate,
              onChange: handleSearchChange('toDueDate', setToDueDate),
              onReset: handleSearchReset('toDueDate', setToDueDate),
            },
          }}
          data={data}
          options={{
            cities,
            streets,
            elevatorPresence,
            shumaStatuses,
            propertyTypes,
            shumaTypes,
            users,
            taskStatuses,
            taskTypes
          }}
          columns={columns}
          page={page}
          setPage={setPage}
          allowSearchByCustomerName={allowSearchByCustomerName}
          allowSearchByShumaID={allowSearchByShumaID}
          preparedDataForExcel={preparedDataForExcel}
          headersForExcel={headersForExcel}
          excelFileName={excelFileName}
          module={module}
          refreshGrid={refreshGrid}
          getListForExcel={async () => await getListForExcel({...requestBody, sortBy: sortColumnName, sortDirection})}
          onSortChange={handleSortChange}
          sortColumnName={sortColumnName}
          sortDirection={sortDirection}
          {...rest}
        />
      </Formik>
    )
    : pendingIndicator;
};

export default DataTableWithFilters;